import {
  CoreApi,
  Product,
  PartKey,
  ProductDesign,
  ProductIdentifiers,
  ProductType,
  getProductReferenceFor,
  InteriorComponent,
  ProductInfoComponent,
  MatchesColorsComponent,
  MetalInteriorComponent,
  RangeComponent,
  IncludedShelfComponent
} from '@inter-ikea-storageone/range';
import { ComponentIds, Entity, HierarchyUtils, ProductPartComponent } from '@inter-ikea-core/core';
import { CornerSolutionShelfReplacementComponent } from '@inter-ikea-storageone/range-pax';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';

export const isMetalInteriorEntity = (entity: Entity) => {
  return entity.hasComponent(MetalInteriorComponent.id);
};

export const isProductOfEquivalentColor = (product: Product, selectedDesign: ProductDesign) => {
  return isEquivalentColor(
    product.type,
    product.design,
    selectedDesign,
    product.meta?.matchesColors
  );
};

export const isEntityOfEquivalentColor = (entity: Entity, selectedDesign: ProductDesign) => {
  const productInfo = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id);
  if (!productInfo) {
    return false;
  }
  return isEquivalentColor(
    productInfo.productType,
    productInfo.design,
    selectedDesign,
    entity.getComponent<MatchesColorsComponent>(MatchesColorsComponent.id)?.colors
  );
};

const isEquivalentColor = (
  productType: ProductType,
  productDesign: ProductDesign,
  selectedDesign: ProductDesign,
  matchesColors?: string[]
): boolean => {
  if (productType === ProductType.BESTA_GLASS_SHELF) {
    return true;
  } else if (matchesColors) {
    return matchesColors.includes(selectedDesign);
  }

  return productDesign?.split('/')[0] === selectedDesign;
};

export const getMajorityColor = (entities: Entity[], designsInGroup: ProductDesign[]) => {
  const metalEntites = entities
    .filter(e => {
      if (e.isDisposed) return false;
      return isMetalInteriorEntity(e);
    })
    .filter(Boolean);
  const otherEntities = entities
    .filter(e => {
      if (e.isDisposed) return false;
      return !isMetalInteriorEntity(e);
    })
    .filter(Boolean);

  const metalColors = metalEntites
    .map(entity => designsInGroup.find(design => isEntityOfEquivalentColor(entity, design)))
    .filter((c): c is ProductDesign => !!c);

  let colors = otherEntities
    .map(entity => {
      const design = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design;
      return design?.split('/')[0];
    })
    .filter((c): c is ProductDesign => !!c);

  if (colors.length === 0) {
    colors = colors.concat(metalColors);
  }

  if (colors.length === 0) return null;

  return getMajorityOfArray(colors) as ProductDesign;
};

export const getMajorityOfArray = (array: string[]) => {
  const occurances: { [color: string]: number } = {};
  let majority = array[0];

  array.forEach(element => {
    if (occurances[element] !== undefined) {
      occurances[element]++;
    } else {
      occurances[element] = 1;
    }
    if (occurances[element] > occurances[majority]) {
      majority = element;
    }
  });
  return majority;
};

export const isEntitiesReplaceable = (
  newDesign: ProductDesign,
  entities: Entity[],
  products: Product[]
): boolean => {
  if (entities.length === 0) return false;

  const replaceableEntities = entities
    .map(entity => {
      if (entity.isDisposed) return null;
      const productRef = getProductReferenceFor(entity);
      const product = products.find(p => p.coreProductId === productRef);

      if (
        product &&
        (!isMetalInteriorEntity(entity) || !isProductOfEquivalentColor(product, newDesign))
      ) {
        return entity;
      } else {
        return null;
      }
    })
    .filter(Boolean);

  return replaceableEntities.length > 0;
};

export const getReplaceableProduct = (
  entity: Entity,
  newDesign: ProductDesign,
  products: Product[]
) => {
  const productRef = getProductReferenceFor(entity);
  const product = products.find(p => p.coreProductId === productRef);
  if (!product) return null;
  const newProduct = products.find(
    p =>
      p.type === product.type &&
      p.communicatedDimensions.width === product.communicatedDimensions.width &&
      p.communicatedDimensions.depth === product.communicatedDimensions.depth &&
      p.communicatedDimensions.height === product.communicatedDimensions.height &&
      p.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT) ===
        product.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT) &&
      isProductOfEquivalentColor(p, newDesign)
  );
  return newProduct || null;
};

export const getFramesInScene = (coreApi: CoreApi, range?: string) => {
  const regularFrames = coreApi.entities
    .getByComponent(ComponentIds.FRAME_COMPONENT_ID)
    .filter(frame =>
      range && range !== ''
        ? frame.getComponent<RangeComponent>(RangeComponent.id)?.range === range
        : true
    )
    .filter(f => {
      const part = f.getComponent<ProductPartComponent>(ComponentIds.PRODUCT_PART_COMPONENT_ID)
        ?.partKey;
      return !(part && (part === PartKey.CORNER_SOLUTION_NORMAL_FRAME || part === PartKey.FRAME));
    });
  const cornerSolutions = coreApi.entities.getByProductType(ProductType.PAX_CORNER_SOLUTION_ASM);
  return [...regularFrames, ...cornerSolutions];
};

export const getInteriorsInFrame = (frame: Entity) => {
  return HierarchyUtils.descendants(frame).filter(
    entity =>
      entity.hasComponent(InteriorComponent.id) &&
      !entity.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
  );
};

export const isIncludedShelfInsideCornerSolution = (entity: Entity) => {
  return (
    (entity.hasComponent(IncludedShelfComponent.id) ||
      entity.hasComponent(CornerSolutionShelfReplacementComponent.id)) &&
    HierarchyUtils.findWithComponentInUpwardsHierarchy(entity, CornerSolutionComponent.id) !== null
  );
};

export const getReplacableOrgainizersInFrame = (frame: Entity) => {
  return HierarchyUtils.descendants(frame).filter(entity => {
    const replaceableOrganisers = [
      ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP,
      ProductType.PAX_SKUBB_SHOE_BOXES,
      ProductType.PAX_SKUBB_STORAGE_CASES,
      ProductType.PAX_SKUBB_HANGING_STORAGE
    ];
    const entityType = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
      ?.productType;
    return !!entityType && replaceableOrganisers.includes(entityType);
  });
};
